import { useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useMergeProjects } from '../../hooks/useProjects';
import type { MergeProjectsResult } from '../../services/api/projects';

export interface ProjectMergeModalProject {
  id: number;
  name: string | null;
}

export interface ProjectMergeModalProps {
  isOpen: boolean;
  projectA: ProjectMergeModalProject;
  projectB: ProjectMergeModalProject;
  onClose: () => void;
  onMerged: (result: MergeProjectsResult) => void;
}

/**
 * Quick 260805-gmk — admin-only "merge two projects" modal. Confirming moves
 * ALL tasks from the chosen source project into the target and optionally
 * archives the source afterward.
 */
export function ProjectMergeModal({
  isOpen,
  projectA,
  projectB,
  onClose,
  onMerged,
}: ProjectMergeModalProps) {
  const [sourceId, setSourceId] = useState(projectA.id);
  const [archiveSource, setArchiveSource] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<MergeConflict[]>([]);
  const [mergeDuplicates, setMergeDuplicates] = useState(false);
  const mergeMutation = useMergeProjects();

  // Only pure duplicates (name AND code hitting the same target task) may be
  // auto-merged; any name-only/code-only collision keeps the merge blocked.
  const allConflictsAreDuplicates =
    conflicts.length > 0 && conflicts.every((c) => c.reason === 'name+code');

  const source = sourceId === projectA.id ? projectA : projectB;
  const target = sourceId === projectA.id ? projectB : projectA;

  const handleSwap = () => {
    setSourceId((prev) => (prev === projectA.id ? projectB.id : projectA.id));
  };

  const handleClose = () => {
    setError(null);
    setConflicts([]);
    setMergeDuplicates(false);
    onClose();
  };

  const handleSwapWithReset = () => {
    handleSwap();
    setConflicts([]);
    setMergeDuplicates(false);
    setError(null);
  };

  const handleConfirm = async () => {
    setError(null);
    setConflicts([]);
    try {
      const result = await mergeMutation.mutateAsync({
        sourceProjectId: source.id,
        targetProjectId: target.id,
        archiveSource,
        mergeDuplicates,
      });
      onMerged(result);
      handleClose();
    } catch (err) {
      if (isMergeConflictError(err) && err.details.conflicts.length > 0) {
        setConflicts(err.details.conflicts.map(normalizeConflict));
        setError('Cannot merge: these tasks collide between the two projects. Nothing was moved.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to merge projects');
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Merge Projects" size="md">
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Source</p>
              <p className="font-medium text-gray-900">{source.name ?? `Project #${source.id}`}</p>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              type="button"
              onClick={handleSwapWithReset}
              aria-label="Swap source and target"
              className="rounded-full border border-gray-300 p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              ⇄
            </button>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Target</p>
              <p className="font-medium text-gray-900">{target.name ?? `Project #${target.id}`}</p>
            </div>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={archiveSource}
            onChange={(e) => setArchiveSource(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Archive source project after merge
        </label>

        <p className="text-sm text-gray-600">
          All tasks from the source project move to the target. Hours already logged stay
          reported under the source project; new work on moved tasks is logged under the
          target.
        </p>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {conflicts.length > 0 && (
          <div className="overflow-auto rounded-lg border border-red-200">
            <table className="min-w-full divide-y divide-red-100 text-sm">
              <thead className="bg-red-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-red-800">
                    {source.name ?? `Project #${source.id}`}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-red-800">
                    {target.name ?? `Project #${target.id}`}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-red-800">Conflict</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-100 bg-white">
                {conflicts.map((c, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-gray-900">
                      {c.source.name}
                      {c.source.code && <span className="ml-1.5 text-xs text-gray-400">{c.source.code}</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      {c.target.name}
                      {c.target.code && <span className="ml-1.5 text-xs text-gray-400">{c.target.code}</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                        {c.reason}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {allConflictsAreDuplicates && (
          <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <input
              type="checkbox"
              checked={mergeDuplicates}
              onChange={(e) => setMergeDuplicates(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>
              <span className="font-medium">It&apos;s OK — these are the same tasks.</span> Merge
              each pair into the target&apos;s task: time entries, comments and attachments are
              combined; the duplicate is removed. Then press Merge projects again.
            </span>
          </label>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={handleClose} disabled={mergeMutation.isPending}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleConfirm} isLoading={mergeMutation.isPending}>
          Merge projects
        </Button>
      </div>
    </Modal>
  );
}

interface MergeConflictTask {
  name: string;
  code: string | null;
}

interface MergeConflict {
  source: MergeConflictTask;
  target: MergeConflictTask;
  reason: string;
}

/** Wire shape: paired since quick-260805-gmk follow-up; the flat `{name, code}`
 *  fields are kept by the backend for compatibility, so normalize both. */
interface WireMergeConflict extends Partial<MergeConflict> {
  name: string;
  code: string | null;
}

function normalizeConflict(c: WireMergeConflict): MergeConflict {
  return {
    source: c.source ?? { name: c.name, code: c.code },
    target: c.target ?? { name: c.name, code: c.code },
    reason: c.reason ?? 'name',
  };
}

/**
 * Narrow helper mirroring `isStateInUseError` (projectStates.ts) — duck-types
 * on `{ statusCode, details }` rather than `instanceof ApiClientError` so it
 * matches both the real `ApiClientError` thrown by `apiPost` and the plain
 * `Object.assign(new Error(...), { statusCode, details })` shape used in
 * component tests.
 */
function isMergeConflictError(
  err: unknown,
): err is { details: { code: string; conflicts: WireMergeConflict[] } } {
  if (!err || typeof err !== 'object') return false;
  const e = err as { statusCode?: number; details?: unknown };
  if (e.statusCode !== 422) return false;
  const d = e.details as { code?: string; conflicts?: unknown } | undefined;
  return d?.code === 'project_merge_conflict' && Array.isArray(d.conflicts);
}
